import React, { useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { BookingCommercialLineRequest } from 'api/types/booking.types';
import type { MealPlan } from 'api/types/mealPlan.types';
import { formatCurrency } from '../booking.utils';

/**
 * Per-pax commercial lines — what the booking SELLS.
 *
 * See crs-doc/CRS-Per-Pax-Commercial-Allocation-Design.md. Deliberately carries
 * no room count and no room category: rooms are stated once on the booking and
 * distributed across categories separately, so changing an allocation (an
 * upgrade) cannot move any figure on this screen.
 *
 * A child tier is just another line — "Child Rate", 0 adults, 5 children, its own
 * rate — which is why there is no separate child-rate field, and why extra bed,
 * infant or complimentary delegate need nothing new either.
 */

export interface CommercialLineRow extends BookingCommercialLineRequest {
  /** Stable client-side key; never sent. */
  _rowKey: string;
}

let _seq = 0;
export const makeCommercialRowKey = () => `cl-${++_seq}`;

export const emptyCommercialLine = (): CommercialLineRow => ({
  _rowKey: makeCommercialRowKey(),
  description: '',
  numAdults: 0,
  numChildren: 0,
  ratePerPax: 0,
  mealAdultRate: 0,
  mealChildRate: 0,
});

interface Props {
  lines: CommercialLineRow[];
  onChange: (lines: CommercialLineRow[]) => void;
  allMealPlans: MealPlan[];
  numNights: number;
  fieldErrors: Record<string, string>;
  disabled?: boolean;
}

/**
 * Mirrors the server's CommercialLineCalculator — meal out first at exact
 * rates, room takes the balance, clamped at zero. Exported so the modal's
 * subtotal and the summary panel price from ONE function; a re-derivation per
 * consumer is how figures learn to disagree.
 */
export const priceCommercialLine = (l: CommercialLineRow, nights: number) => {
  const pax = (l.numAdults || 0) + (l.numChildren || 0);
  const amount = (l.ratePerPax || 0) * pax * nights;
  let meal = ((l.mealAdultRate || 0) * (l.numAdults || 0)
    + (l.mealChildRate || 0) * (l.numChildren || 0)) * nights;
  let room = amount - meal;
  if (room < 0) { meal = amount; room = 0; }
  return { amount, room, meal, pax };
};

const CommercialLinesSection: React.FC<Props> = ({
  lines, onChange, allMealPlans, numNights, fieldErrors, disabled,
}) => {
  const update = useCallback((key: string, patch: Partial<CommercialLineRow>) => {
    onChange(lines.map(l => (l._rowKey === key ? { ...l, ...patch } : l)));
  }, [lines, onChange]);

  const totals = useMemo(() => lines.reduce((acc, l) => {
    const p = priceCommercialLine(l, numNights);
    return {
      amount: acc.amount + p.amount,
      meal: acc.meal + p.meal,
      pax: acc.pax + p.pax,
    };
  }, { amount: 0, meal: 0, pax: 0 }), [lines, numNights]);

  return (
    <div>
      <hr className="booking-section-divider" />
      <div className="booking-section-title">
        <IconSystem.tag size={15} />
        Commercial Lines
        <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          What the group is charged — room allocation is separate
        </span>
      </div>

      {fieldErrors['commercialLines'] && (
        <div className="form-error-message" style={{ marginBottom: '0.4rem' }}>
          {fieldErrors['commercialLines']}
        </div>
      )}

      <div className="cl-wrap">
        <table className="cl-grid">
          <thead>
            <tr>
              <th>Description</th>
              <th className="n">Adults</th>
              <th className="n">Children</th>
              <th>Meal</th>
              <th className="n">Rate / pax</th>
              <th className="n">Meal (of which)</th>
              <th className="n">Amount</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map(l => {
              const p = priceCommercialLine(l, numNights);
              const noPax = p.pax === 0;
              return (
                <tr key={l._rowKey} className={noPax ? 'is-empty' : undefined}>
                  <td>
                    <input
                      type="text"
                      className="cl-cell cl-cell-t"
                      value={l.description}
                      onChange={(e) => update(l._rowKey, { description: e.target.value })}
                      placeholder="e.g. 20 Rooms with Twin Sharing"
                      maxLength={150}
                      disabled={disabled}
                    />
                  </td>
                  <td className="n">
                    <input
                      type="number" min={0} className="cl-cell"
                      value={l.numAdults}
                      onChange={(e) => update(l._rowKey, { numAdults: Math.max(0, Number(e.target.value) || 0) })}
                      disabled={disabled}
                    />
                  </td>
                  <td className="n">
                    <input
                      type="number" min={0} className="cl-cell"
                      value={l.numChildren}
                      onChange={(e) => update(l._rowKey, { numChildren: Math.max(0, Number(e.target.value) || 0) })}
                      disabled={disabled}
                    />
                  </td>
                  <td>
                    <select
                      className="cl-cell cl-cell-t"
                      value={l.mealPlanId ?? ''}
                      onChange={(e) => {
                        const mp = allMealPlans.find(m => m.id === e.target.value);
                        update(l._rowKey, {
                          mealPlanId: mp?.id,
                          mealPlanCode: mp?.planCode ?? '',
                          mealAdultRate: mp?.adultRate ?? 0,
                          mealChildRate: mp?.childRate ?? 0,
                        });
                      }}
                      disabled={disabled}
                    >
                      <option value="">—</option>
                      {allMealPlans.map(m => (
                        <option key={m.id} value={m.id}>{m.planCode}</option>
                      ))}
                    </select>
                  </td>
                  <td className="n">
                    <input
                      type="number" min={0} step="0.01" className="cl-cell cl-cell-w"
                      value={l.ratePerPax}
                      onChange={(e) => update(l._rowKey, { ratePerPax: Math.max(0, Number(e.target.value) || 0) })}
                      disabled={disabled}
                    />
                  </td>
                  <td className="n cl-mut">{formatCurrency(p.meal)}</td>
                  <td className="n cl-amount">{formatCurrency(p.amount)}</td>
                  <td className="cl-actions">
                    <button
                      type="button"
                      title="Remove line"
                      onClick={() => onChange(lines.filter(x => x._rowKey !== l._rowKey))}
                      disabled={disabled}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}

            {/* A line with no pax prices nothing. Flagged rather than silently
                contributing zero — the server rejects it too. */}
            {lines.some(l => (l.numAdults + l.numChildren) === 0) && (
              <tr className="cl-warn">
                <td colSpan={8}>
                  A line with no adults or children prices nothing — set a headcount or remove it.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}>
                {lines.length} line{lines.length !== 1 ? 's' : ''} · {totals.pax} pax · {numNights} night{numNights !== 1 ? 's' : ''}
              </td>
              <td className="n cl-mut">{formatCurrency(totals.meal)}</td>
              <td className="n cl-amount">{formatCurrency(totals.amount)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <button
        type="button"
        className="cl-add"
        onClick={() => onChange([...lines, emptyCommercialLine()])}
        disabled={disabled}
      >
        <IconSystem.add size={12} /> Add line
      </button>

      <div className="cl-hint">
        The rate applies to each line's own headcount — a child rate is simply its own
        line. Rooms are stated once below and allocated across categories separately,
        so an upgrade never changes these figures.
      </div>
    </div>
  );
};

export default CommercialLinesSection;
